/**
 * Reactive view of the offline write queue.
 *
 * A queue with no UI is indistinguishable from data loss — the user types a
 * comment, it disappears from the composer, and nothing tells them whether it
 * reached the server. Everything here exists to answer "what is still waiting,
 * and what went wrong".
 */
import type { OfflineLedgerEntry } from '@/lib/offline/offline-queue'
import { useCallback, useSyncExternalStore } from 'react'
import { offlineLedger } from '@/lib/offline/ledger'
import {
  selectBlockedWrites,
  selectPendingWrites,
} from '@/lib/offline/offline-queue'
import {
  discardOfflineWrite,
  retryOfflineWrite,
} from '@/lib/offline/queue-actions'
import { useQueryClient } from '@tanstack/react-query'

// useSyncExternalStore compares snapshots by reference and `ledger.list()`
// builds a fresh array each call, so cache it per ledger revision or the hook
// re-renders forever.
let cachedSnapshot: OfflineLedgerEntry[] = []
let cachedSignature = ''

function getSnapshot(): OfflineLedgerEntry[] {
  const entries = offlineLedger.list()
  const signature = entries
    .map(
      entry =>
        `${entry.envelope.idempotencyKey}:${entry.status}:${entry.attempts}`,
    )
    .join('|')

  if (signature !== cachedSignature) {
    cachedSignature = signature
    cachedSnapshot = entries
  }
  return cachedSnapshot
}

function subscribe(listener: () => void): () => void {
  return offlineLedger.subscribe(listener)
}

export interface PendingWritesState {
  /** Everything not yet confirmed by the server, oldest first. */
  pending: OfflineLedgerEntry[]
  /** The subset that will not move without the user: failed, stale, ambiguous. */
  blocked: OfflineLedgerEntry[]
  count: number
  blockedCount: number
  retry: (entry: OfflineLedgerEntry) => Promise<void>
  discard: (entry: OfflineLedgerEntry) => void
}

export function usePendingWrites(): PendingWritesState {
  const queryClient = useQueryClient()
  const entries = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const pending = selectPendingWrites(entries)
  const blocked = selectBlockedWrites(entries)

  const retry = useCallback(
    (entry: OfflineLedgerEntry) =>
      retryOfflineWrite(queryClient, offlineLedger, entry),
    [queryClient],
  )

  const discard = useCallback(
    (entry: OfflineLedgerEntry) => {
      discardOfflineWrite(queryClient, offlineLedger, entry)
    },
    [queryClient],
  )

  return {
    pending,
    blocked,
    count: pending.length,
    blockedCount: blocked.length,
    retry,
    discard,
  }
}
